//@flow

import * as React from "react"
import { colorsForLevel } from "../Shared/AQILegend"
import { TweenMax, Cubic, Elastic } from "gsap"

import type { SampleData } from "./HistoryModal"

type CoordData = {
  label: string,
  value: number,
  x: number,
  y: number,
  labelX: number,
  labelY: number
}

type Props = {
  values: SampleData,
  delay: number,
  reversed?: boolean
}

type State = {
  width: number,
  height: number
}

class Graph extends React.Component<Props, State> {
  container: ?HTMLElement
  line: ?SVGPathElement
  labels: Array<HTMLElement | SVGElement>
  readings: Array<HTMLElement | SVGElement>
  readingPreviews: Array<HTMLElement | SVGElement>
  coords: Array<CoordData>
  labelHeight: number
  labelPadding: number
  radius: number
  maxAQI: number
  minAQI: number
  strokeTween: TweenMax
  animationState: {
    strokeDashoffset: number
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      width: 0,
      height: 0
    }
    this.coords = props.values.map(({ occurredAt, value }, index) => ({
      label: occurredAt,
      value: value,
      x: 0,
      y: 0,
      labelX: 0,
      labelY: 0
    }))
    this.labelHeight = 16
    this.labelPadding = 10
    this.radius = 23
    this.minAQI = 0
    this.animationState = {
      strokeDashoffset: -1
    }
    this.readingPreviews = []
    this.readings = []
    this.labels = []
  }

  componentDidUpdate = () => {
    this.animateStroke()
  }

  componentDidMount() {
    const { values } = this.props
    const height = this.container ? this.container.offsetHeight : 0
    const width = this.container ? this.container.offsetWidth : 0
    const radius = this.radius
    const workingValues = values.map(({ value }) => value)
    const maxAQI = Math.max(Math.max(...workingValues), 1)
    const minAQI = Math.max(Math.min(...workingValues), 0)
    this.coords =
      height > 0 && width > 0 && values
        ? values.map(({ occurredAt, value }, index) => {
            const x = Math.min(
              width - radius,
              radius + (width - radius) / values.length * index + radius * index
            )
            const offsetPercent = (value - minAQI) / (maxAQI - minAQI)
            const workingHeight =
              height - radius - this.labelHeight - this.labelPadding
            const y = Math.min(
              workingHeight,
              Math.max(workingHeight - offsetPercent * workingHeight, radius)
            )
            const labelX = x - radius
            const labelY = height - this.labelHeight
            return {
              label: occurredAt,
              value: Math.round(value),
              x,
              y,
              labelX,
              labelY
            }
          })
        : this.coords

    this.animateReadings()
    this.animatePreviews()
    this.animateLabels()
    this.setState(() => ({
      width,
      height
    }))
  }

  componentWillUnmount() {
    this.strokeTween && this.strokeTween.kill()
  }

  animateStroke = () => {
    const { delay, reversed } = this.props
    const { strokeDashoffset } = this.animationState
    const lineLength = this.line ? this.line.getTotalLength() : 0
    if (lineLength > 0 && strokeDashoffset < 0) {
      this.animationState.strokeDashoffset = lineLength
      TweenMax.set(this.line, {
        strokeDashoffset: `${lineLength}`,
        strokeDasharray: `${lineLength}`
      })
      this.strokeTween = TweenMax.to(this.animationState, 1, {
        strokeDashoffset: reversed ? lineLength * 2 : 0,
        ease: Cubic.easeInOut,
        delay,
        onUpdate: () => {
          TweenMax.set(this.line, {
            strokeDashoffset: `${this.animationState.strokeDashoffset}px`
          })
        }
      })
    }
  }

  animateReadings = () => {
    const { delay, reversed } = this.props
    TweenMax.set(this.readings, {
      opacity: 0
    })
    TweenMax.staggerTo(
      this.readings,
      0.5,
      {
        ease: Cubic.easeOut,
        opacity: 1,
        delay: delay + 0.5
      },
      (reversed ? -1 : 1) * 0.15
    )
  }

  animateLabels = () => {
    const { delay, reversed } = this.props
    TweenMax.set(this.labels, {
      opacity: 0,
      x: "-=10"
    })
    TweenMax.staggerTo(
      this.labels,
      0.25,
      {
        ease: Cubic.easeOut,
        opacity: 1,
        x: 0,
        delay: delay
      },
      (reversed ? -1 : 1) * 0.1
    )
  }

  animatePreviews = () => {
    const { delay, reversed } = this.props
    TweenMax.set(this.readingPreviews, {
      opacity: 0,
      y: "-=10"
    })
    TweenMax.staggerTo(
      this.readingPreviews,
      1,
      {
        ease: Elastic.easeOut,
        opacity: 1,
        y: 0,
        delay: delay
      },
      (reversed ? -1 : 1) * 0.15
    )
  }

  render() {
    return (
      <div
        style={{ height: "100%", width: "100%" }}
        ref={el => (this.container = el)}
      >
        <svg
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style>
            {`.reading { 
              font-family:"din-condensed"; 
              font-size: 32px; 
              font-weight: 400; 
              text-align: center;
              margin: 0;
              padding: ${this.radius - 32 / 2}px 0;
              line-height: 32px;
            }
            .preview {
              fill: #75BAE5;
            }
            .label {
              color: #fff;
              font-family:"din-condensed"; 
              font-size: 16px; 
              font-weight: 400; 
              text-align: center;
              margin: 0;
              line-height: 16px;
            }`}
          </style>
          <defs>
            <g id="Reading">
              <circle
                style={{
                  fill: "inherit"
                }}
                r={this.radius}
              />
            </g>
          </defs>
          <path
            ref={el => (this.line = el)}
            d={this.coords
              .map(({ x, y }, index) => `${index < 1 ? "M" : "L"} ${x} ${y}`)
              .join(" ")}
            stroke="#75BAE5"
            fill="transparent"
            strokeWidth={2}
          />
          {this.coords.map(({ label, value, x, y, labelX, labelY }, index) => {
            return (
              <use
                x={x}
                y={y}
                xlinkHref="#Reading"
                className="preview"
                key={`dataPoint${index}`}
                ref={el => this.readingPreviews.push(el)}
              />
            )
          })}
          {this.coords.map(({ label, value, x, y, labelX, labelY }, index) => {
            const { foreground, background } = colorsForLevel(value)
            return (
              <g key={`dataPoint${index}`} ref={el => this.readings.push(el)}>
                <use x={x} y={y} xlinkHref="#Reading" fill={background} />
                <foreignObject
                  width={this.radius * 2}
                  height={this.radius * 2}
                  x={x - this.radius}
                  y={y - this.radius}
                >
                  <p
                    xmlns="http://www.w3.org/1999/xhtml"
                    className="reading"
                    style={{
                      color: foreground,
                      fontSize: value >= 100 ? "24px" : "32px"
                    }}
                  >
                    {value > 0 ? value : "?"}
                  </p>
                </foreignObject>
                <foreignObject width="46" height="16" x={labelX} y={labelY}>
                  <p
                    xmlns="http://www.w3.org/1999/xhtml"
                    className="label"
                    ref={el => this.labels.push(el)}
                  >
                    {label}
                  </p>
                </foreignObject>
              </g>
            )
          })}
        </svg>
      </div>
    )
  }
}

export default Graph



// WEBPACK FOOTER //
// ./src/Kiosk/HistoryModal/Graph.js